/* eslint-disable react/no-unstable-nested-components --
 * MasterDataTable の columns 配列に並ぶ cell 関数は React component ではなく
 * テーブルセルの render 関数。
 */
import { useTranslation } from 'react-i18next'

import { Badge, HStack, Text } from '@enechain/ecloud-designsystem'

import { HintTooltip } from '~/components/common/HintTooltip'
import {
  AuditCell,
  EditIconButton,
  MasterDataTable,
  type MasterDataTableColumn,
} from '~/components/common/MasterDataTable'
import { convertAreaLabel } from '~/models/area'
import {
  selfRoleHint,
  selfRoleLabel,
  supplyFormHint,
  supplyFormLabel,
} from '~/pages/AdminOcctoMaster/tabs/SplitSupplyContract/models/labels'
import type { SplitSupplyContractRow } from '~/pages/AdminOcctoMaster/tabs/SplitSupplyContract/types'

type Props = {
  rows: SplitSupplyContractRow[]
  isLoading: boolean
  onEdit: (row: SplitSupplyContractRow) => void
  /** 契約が 1 件でもあるか。 絞り込みで 0 件になった時は「該当するデータがありません」を出す。 */
  hasContracts: boolean
  /** 一覧取得エラー時のメッセージ。 */
  errorLabel?: string
}

const NO_VALUE = '—'

/**
 * 分割供給契約の一覧。 並びは供給地点特定番号の昇順 (backend が並べて返す)。
 * 列は エリア / 供給地点特定番号 / 需要家 / 供給形態 / 負荷追随分供給者 / ベース分供給者 /
 * 自社の立場 / 最終更新。 行クリックで編集。
 */
export const SplitSupplyContractTable = ({
  rows,
  isLoading,
  onEdit,
  hasContracts,
  errorLabel,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  const columns: Array<MasterDataTableColumn<SplitSupplyContractRow>> = [
    {
      key: 'areaCode',
      header: t('page:adminOcctoMaster.splitSupplyContract.column.areaCode'),
      cell: (row) =>
        row.areaCode === undefined ? NO_VALUE : convertAreaLabel(row.areaCode),
    },
    {
      key: 'supplyPointSpecificNumber',
      header: t(
        'page:adminOcctoMaster.splitSupplyContract.column.supplyPointSpecificNumber',
      ),
      nowrap: true,
      cell: (row) => row.supplyPointSpecificNumber,
    },
    {
      key: 'customerName',
      header: t(
        'page:adminOcctoMaster.splitSupplyContract.column.customerName',
      ),
      cell: (row) => (
        <HintTooltip
          content={
            row.customerName === undefined
              ? t('page:adminOcctoMaster.splitSupplyContract.noCustomerHint')
              : undefined
          }
        >
          <span>{row.customerName ?? NO_VALUE}</span>
        </HintTooltip>
      ),
    },
    {
      key: 'supplyForm',
      header: (
        <HintTooltip
          content={t(
            'page:adminOcctoMaster.splitSupplyContract.columnHint.supplyForm',
          )}
        >
          <span>
            {t('page:adminOcctoMaster.splitSupplyContract.column.supplyForm')}
          </span>
        </HintTooltip>
      ),
      cell: (row) => (
        <HintTooltip
          content={supplyFormHint(
            t,
            row.supplyForm,
            row.ownSlotFrom,
            row.ownSlotTo,
          )}
        >
          <Badge status="neutral">{supplyFormLabel(t, row.supplyForm)}</Badge>
        </HintTooltip>
      ),
    },
    {
      key: 'loadFollowing',
      header: (
        <HintTooltip
          content={t(
            'page:adminOcctoMaster.splitSupplyContract.columnHint.loadFollowing',
          )}
        >
          <span>
            {t(
              'page:adminOcctoMaster.splitSupplyContract.column.loadFollowing',
            )}
          </span>
        </HintTooltip>
      ),
      cell: (row) => <BusinessCell business={row.loadFollowing} />,
    },
    {
      key: 'base',
      header: (
        <HintTooltip
          content={t(
            'page:adminOcctoMaster.splitSupplyContract.columnHint.base',
          )}
        >
          <span>
            {t('page:adminOcctoMaster.splitSupplyContract.column.base')}
          </span>
        </HintTooltip>
      ),
      cell: (row) => <BusinessCell business={row.base} />,
    },
    {
      key: 'selfRole',
      align: 'center',
      header: t('page:adminOcctoMaster.splitSupplyContract.column.selfRole'),
      cell: (row) => (
        <HintTooltip content={selfRoleHint(t, row.selfRole)}>
          <Badge status="neutral">{selfRoleLabel(t, row.selfRole)}</Badge>
        </HintTooltip>
      ),
    },
    {
      key: 'updatedAt',
      header: t('page:adminOcctoMaster.splitSupplyContract.column.updatedAt'),
      nowrap: true,
      cell: (row) => (
        <AuditCell updatedAt={row.updatedAt} updatedBy={row.updatedBy} />
      ),
    },
  ]

  return (
    <MasterDataTable
      columns={columns}
      emptyLabel={t(
        hasContracts
          ? 'page:adminOcctoMaster.splitSupplyContract.emptyFiltered'
          : 'page:adminOcctoMaster.splitSupplyContract.empty',
      )}
      errorLabel={errorLabel}
      getRowKey={(row) => row.id}
      isLoading={isLoading}
      onRowClick={onEdit}
      renderRowActions={(row) => <EditIconButton onClick={() => onEdit(row)} />}
      rows={rows}
    />
  )
}

const BusinessCell = ({
  business,
}: {
  business: { code: string; name: string }
}): React.ReactElement => (
  <HStack alignItems="baseline" gap={1}>
    <Text as="span">{business.code}</Text>
    {business.name !== '' && (
      <Text as="span" textStyle="caption" color="Text.lowEmphasis">
        {business.name}
      </Text>
    )}
  </HStack>
)
